/**
 * RewardsTab — Types de coupon + Récompenses catalogue.
 * Les liens coupon ↔ rewards se gèrent depuis le type de coupon (PUT …/rewards).
 */
import React, { useState } from "react";
import {
    Box, Button, Tabs, Tab, Typography, Divider,
} from "@mui/material";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import AddIcon from "@mui/icons-material/Add";

import { SECTION_COLORS } from "../../constants";

import CouponTypesSection from "./couponTypes/CouponTypesSection";
import CreateCouponTypeDialog from "./couponTypes/CreateCouponTypeDialog";
import EditCouponTypeDialog from "./couponTypes/EditCouponTypeDialog";
import ViewCouponTypeDialog from "./couponTypes/ViewCouponTypeDialog";
import DeleteCouponTypeDialog from "./couponTypes/DeleteCouponTypeDialog";

import RewardsListSection from "./RewardsListSection";
import CreateRewardDialog from "./CreateRewardDialog";
import EditRewardDialog from "./EditRewardDialog";
import ViewRewardDialog from "./ViewRewardDialog";
import DeleteRewardDialog from "./DeleteRewardDialog";

const SUB_TABS = [
    { label: "Types de Coupon", icon: <ConfirmationNumberIcon sx={{ fontSize: 15 }} /> },
    { label: "Récompenses", icon: <CardGiftcardIcon sx={{ fontSize: 15 }} /> },
];

const RewardsTab = ({
    couponTypesApi,
    rewardsApi,
    onDeleteCouponTypeConfirmed,
    deletingCouponType,
    onDeleteRewardConfirmed,
    onDeactivateReward,
    onCloseDeleteReward,
    deletingReward,
    deactivatingReward,
    deleteRewardError,
    notify,
}) => {
    const [subTab, setSubTab] = useState(0);

    const [createCouponTypeOpen, setCreateCouponTypeOpen] = useState(false);
    const [viewCouponType, setViewCouponType] = useState(null);
    const [editCouponType, setEditCouponType] = useState(null);
    const [deleteCouponType, setDeleteCouponType] = useState(null);

    const [createRewardOpen, setCreateRewardOpen] = useState(false);
    const [viewReward, setViewReward] = useState(null);
    const [editReward, setEditReward] = useState(null);
    const [deleteReward, setDeleteReward] = useState(null);

    const [pages, setPages] = useState([0, 0]);
    const handlePageChange = (idx) => (_, newPage) =>
        setPages((prev) => { const n = [...prev]; n[idx] = newPage; return n; });

    const addActions = [
        { label: "Ajouter un Type de Coupon", action: () => setCreateCouponTypeOpen(true) },
        { label: "Ajouter une Récompense", action: () => setCreateRewardOpen(true) },
    ];

    const couponTypesList = Array.isArray(couponTypesApi?.data) ? couponTypesApi.data : [];

    return (
        <>
            <CreateCouponTypeDialog
                open={createCouponTypeOpen}
                onClose={() => setCreateCouponTypeOpen(false)}
                onCreated={() => { couponTypesApi.refetch(); notify("Type de coupon créé avec succès"); }}
            />
            <ViewCouponTypeDialog
                open={Boolean(viewCouponType)} couponType={viewCouponType}
                onClose={() => setViewCouponType(null)} onEditRequest={setEditCouponType}
            />
            <EditCouponTypeDialog
                open={Boolean(editCouponType)} couponType={editCouponType}
                onClose={() => setEditCouponType(null)}
                onSaved={() => { couponTypesApi.refetch(); rewardsApi.refetch(); notify("Type de coupon mis à jour"); }}
            />
            <DeleteCouponTypeDialog
                open={Boolean(deleteCouponType)} couponType={deleteCouponType}
                onClose={() => !deletingCouponType && setDeleteCouponType(null)}
                onConfirm={() => onDeleteCouponTypeConfirmed(deleteCouponType, setDeleteCouponType)}
                deleting={deletingCouponType}
            />

            <CreateRewardDialog
                open={createRewardOpen}
                onClose={() => setCreateRewardOpen(false)}
                onCreated={() => { rewardsApi.refetch(); notify("Récompense créée — liez-la au type de coupon si besoin"); }}
                couponTypes={couponTypesList}
            />
            <ViewRewardDialog
                open={Boolean(viewReward)} reward={viewReward}
                onClose={() => setViewReward(null)} onEditRequest={setEditReward}
                couponTypes={couponTypesList}
            />
            <EditRewardDialog
                open={Boolean(editReward)} reward={editReward}
                onClose={() => setEditReward(null)}
                onSaved={() => { rewardsApi.refetch(); notify("Récompense mise à jour"); }}
                couponTypes={couponTypesList}
            />
            <DeleteRewardDialog
                open={Boolean(deleteReward)} reward={deleteReward}
                onClose={() => onCloseDeleteReward?.(setDeleteReward) ?? (!deletingReward && setDeleteReward(null))}
                onConfirm={() => onDeleteRewardConfirmed(deleteReward, setDeleteReward)}
                onDeactivate={() => onDeactivateReward?.(deleteReward, setDeleteReward)}
                deleting={deletingReward}
                deactivating={deactivatingReward}
                deleteError={deleteRewardError}
            />

            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
                <Tabs
                    value={subTab}
                    onChange={(_, v) => setSubTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        minHeight: 36,
                        "& .MuiTabs-flexContainer": { gap: 0.5 },
                        "& .MuiTab-root": {
                            textTransform: "none", fontWeight: 600, fontFamily: "Montserrat, sans-serif",
                            fontSize: 12, minHeight: 36, borderRadius: 2, px: 1.5, color: "#64748b",
                        },
                        "& .Mui-selected": {
                            color: `${SECTION_COLORS.rewards.main} !important`,
                            bgcolor: `${SECTION_COLORS.rewards.main}12`,
                        },
                        "& .MuiTabs-indicator": {
                            height: 2, borderRadius: "2px 2px 0 0", background: SECTION_COLORS.rewards.gradient,
                        },
                    }}
                >
                    {SUB_TABS.map((t, idx) => (
                        <Tab key={idx} label={<Box display="flex" alignItems="center" gap={0.6}>{t.icon}{t.label}</Box>} />
                    ))}
                </Tabs>

                <Button
                    size="small" variant="contained" startIcon={<AddIcon />}
                    onClick={addActions[subTab].action}
                    sx={{
                        textTransform: "none", fontWeight: 600, fontFamily: "Montserrat, sans-serif",
                        fontSize: 12, borderRadius: 2, background: SECTION_COLORS.rewards.gradient,
                    }}
                >
                    {addActions[subTab].label}
                </Button>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {subTab === 0 && (
                <CouponTypesSection
                    couponTypesApi={couponTypesApi}
                    page={pages[0]}
                    onPageChange={handlePageChange(0)}
                    onView={setViewCouponType}
                    onEdit={setEditCouponType}
                    onDelete={setDeleteCouponType}
                />
            )}
            {subTab === 1 && (
                <RewardsListSection
                    rewardsApi={rewardsApi}
                    couponTypes={couponTypesList}
                    page={pages[1]}
                    onPageChange={handlePageChange(1)}
                    onView={setViewReward}
                    onEdit={setEditReward}
                    onDelete={setDeleteReward}
                />
            )}
        </>
    );
};

export default RewardsTab;
